import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "indented", "setIsOpen", "open", "selectedPage", "useSidePanel"];
import { Children, useCallback, useMemo, useState } from 'react';
import styled from 'foundations-theming/utils';
import { Panel } from 'foundations-components/transitional/components/Panel';
import FullScreenModal from './FullScreenModal';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { Button } from 'foundations-components/transitional/components/Button';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ItemsWrapper = styled.div.withConfig({
  displayName: "MobileNestedMenu__ItemsWrapper"
})(["", " & button:not([data-no-full-width=\"true\"]){width:100%;}> *{padding:0;", " border-bottom:1px solid ", ";}"], props => props.$indented && `margin-left: ${props.theme.space['500']};`, props => props.$indented && `padding: ${props.theme.space['300']} ${props.theme.space['600']} ${props.theme.space['300']} 0;`, props => props.theme.color['border-core-subtle']);
const BacklinkText = styled.div.withConfig({
  displayName: "MobileNestedMenu__BacklinkText"
})(["color:", ";font-size:", ";font-weight:", ";"], ({
  theme
}) => theme.color['text-inverse-default'], ({
  theme
}) => theme.typography['heading-50'].fontSize, ({
  theme
}) => theme.typography['heading-50'].fontWeight);
const MobileNestedMenu = _ref => {
  let {
      children,
      indented,
      setIsOpen,
      open,
      selectedPage = -1,
      useSidePanel = true
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [page, showPage] = useState(selectedPage);
  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const flatChildren = useMemo(() => {
    const allChildren = [];
    Children.toArray(children).forEach(child => {
      const item = child;
      if (item.props.nestedOptions) {
        if (Array.isArray(item.props.children)) {
          allChildren.push(...item.props.children);
        } else {
          allChildren.push(item.props.children);
        }
      } else {
        allChildren.push(child);
      }
    });
    return allChildren;
  }, [children]);
  const pages = useMemo(() => {
    return flatChildren.map(child => {
      if (!child) return '';
      const item = child;
      const {
        props: {
          subpage
        } = {}
      } = item;
      return subpage || '';
    }) || [];
  }, [flatChildren]);
  const headerContent = useMemo(() => {
    return /*#__PURE__*/_jsxs(Flex, {
      inlineSize: "100%",
      justifyContent: "space-between",
      children: [page > 0 ? /*#__PURE__*/_jsx(Button, {
        marginInlineStart: "-1000",
        onClick: () => showPage(-1),
        paddingInline: "400",
        responsive: false,
        use: "unstyled",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(LeftIcon, {
            color: "text-inverse-default",
            size: "300"
          }), /*#__PURE__*/_jsx(BacklinkText, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.common.back"
            })
          })]
        })
      }) : /*#__PURE__*/_jsx(View, {}), /*#__PURE__*/_jsx(DialogCloseButton, {
        "data-test-id": "nested-menu-close-btn",
        insetBlockStart: "600",
        onClick: close
      })]
    });
  }, [close, page]);
  const body = useMemo(() => {
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ItemsWrapper, {
        $indented: indented,
        children: page < 0 ? flatChildren.map((child, key) => {
          return child && /*#__PURE__*/_jsx("div", {
            onClick: () => pages[key] && showPage(key),
            role: "menuitem",
            children: child
          }, key);
        }) : pages[page]
      })
    );
  }, [flatChildren, indented, page, pages]);
  if (useSidePanel) {
    return /*#__PURE__*/_jsxs(Panel, {
      width: "100%",
      children: [/*#__PURE__*/_jsx(PanelHeader, {
        children: headerContent
      }), body]
    });
  }
  const header = /*#__PURE__*/_jsx(PanelHeader, {
    children: /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      blockSize: "40px",
      children: headerContent
    })
  });
  return /*#__PURE__*/_jsx(FullScreenModal, Object.assign({
    header: header,
    open: open
  }, restProps, {
    children: body
  }));
};
export default MobileNestedMenu;